import { computed, defineComponent, PropType, ref } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapActions, mapState } from "pinia";
import moment from "moment";

import { useApp } from "../../../../App/useApp";
import { useConversationStore } from "../../../store/useConversationStore";
import { useUserStore } from "../../../store/useUsersStore";
import { useEditorStore } from "../../../store/useEditorStore";
import { AutoGeneratedMessageEnum } from "../../../../../enums/AutoGeneratedMessageEnum";
import type {
    IMessageItem,
    IMessagePlaceholder,
    IReplyToMessage
} from "../../../../../resources-vue/vue-interfaces/messaging-service/i-message";
import {
    formatMessageTime,
    truncateString,
    formatTaggedUserMessage,
    sortPlaceholdersList
} from "../../../../../common/vue-helpers/modifiers/format-message";
import { markdownToHtml } from "../../../../../common/vue-helpers/modifiers/markdown-to-html-modifier";
import {
    formatLocalDateWithMeridiem,
    sanitizeMarkup
} from "../../../../../common/vue-helpers/modifiers/value-modifier";
import eventBus from "../../../../../utilities/eventBus";
import { MessageService } from "../../../../../services/message-service";
import { ToastrService } from "../../../../../services/toastr.service";
import { NavigationService } from "../../../../../services/navigation.service";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import config from "../../../../../common/config";
import {
    ParentPermissionEnum,
    PermissionActionEnum,
    PlaceholdersListEnum,
    ApplicationEnum,
    ConversationTypeEnum,
    AvaInitialsType
} from "../../../../../enums/enums";
import { getHospiceRedirectionUrl } from "../../../../../common/get-hospice-redirection-url";

import Alert from "../../../../../resources-vue/vue-dialogs/Alert/Alert.vue";
import MessageReactions from "../MessageReactions/MessageReaction.vue";
import EmojiPicker from "../../../../../resources-vue/vue-custom-elements/EmojiPicker/EmojiPicker.vue";
import MessageHoverActions from "../MessageHoverActions/MessageHoverActions.vue";
import AvaInitials from "../../../components/AvaInitials/AvaInitials.vue";

export default defineComponent({
    components: {
        MessageReactions,
        EmojiPicker,
        MessageHoverActions,
        AvaInitials
    },
    props: {
        message: { type: Object as PropType<IMessageItem>, default: {} as IMessageItem },
        previousMessage: {
            type: Object as PropType<IMessageItem>,
            default: null
        }
    },
    emits: ["selectAllUpdated", "IsReplyMessage"],
    data() {
        return {
            _messageService: null as MessageService,
            _navigationService: null as NavigationService,
            _toastrService: null as ToastrService,
            _permissionManager: null as PermissionManager,
            fetchedAssets: [],
            dialogRef: ref(null),
            assetLoading: false as boolean,
            showDownloadBtn: false as boolean,
            AutoGeneratedMessageEnum: AutoGeneratedMessageEnum,
            showTaggedSign: false as boolean,
            highlightTaggedUser: false as boolean,
            sharedProfilesList: [] as IMessagePlaceholder[],
            taggedUsersList: [] as IMessagePlaceholder[],
            replyMsgProfiles: [] as IMessagePlaceholder[],
            defaultQuickReactions: ["👍", "❤️", "😂", "😮"], // 4 default emojis,
            ConversationTypeEnum,
            AvaInitialsType,
            formatMessageTime,
            markdownToHtml,
            sanitizeMarkup,
            truncateString,
            formatLocalDateWithMeridiem
        };
    },
    computed: {
        ...mapState(useApp, ["loggedInUserDetails"]),
        ...mapState(useConversationStore, { activeConversation: "getActiveConversation" }),
        ...mapState(useUserStore, ["getUser", "getLoginIdByUserId"]),
        ...mapState(useEditorStore, ["editorMessage"]),

        formattedMessage() {
            const { text, showTagSignForMsg } = formatTaggedUserMessage(
                this.message.text,
                this.taggedUsersList,
                this.loggedInUserDetails.loginId
            );
            this.showTaggedSign = showTagSignForMsg;

            return text;
        },
        shouldHideMessageInfo(): boolean {
            if (!this.previousMessage) return false;
            if (this.message.isAutoGenerated || this.message.isDeprecated) return false;

            const prevUserId = this.previousMessage.senderUserId;
            const currUserId = this.message.senderUserId;
            if (!prevUserId || !currUserId) return false;

            const prevCreated = this.previousMessage.createdOn;
            const currCreated = this.message.createdOn;
            if (!prevCreated || !currCreated) return false;
            const prevTime = moment(prevCreated).format("YYYY-MM-DD HH:mm");
            const currTime = moment(currCreated).format("YYYY-MM-DD HH:mm");

            return prevUserId === currUserId && prevTime === currTime;
        }
    },
    async mounted() {
        if (this.message.assetIds.length > 0) {
            await this.renderAsset(this.activeConversation.id, this.message.id, this.message.assetIds);
        }
        if (this.message?.replyToMessage) {
            this.setRepliedMsgProfiles();
        }
        this.updatePlaceholdersList();
    },
    created() {
        this._messageService = Container.instance.get(MessageService);
        this._navigationService = Container.instance.get(NavigationService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._toastrService = Container.instance.get(ToastrService);
    },
    provide() {
        return {
            dialogRef: computed(() => this.dialogRef.value)
        };
    },
    watch: {
        async "message.assetIds"(newVal, oldVal) {
            if (newVal?.length !== oldVal?.length && newVal?.length > 0) {
                await this.renderAsset(this.activeConversation.id, this.message.id, newVal);
            }
        },
        "message.placeHoldersList"() {
            this.updatePlaceholdersList();
        }
    },

    methods: {
        ...mapActions(useEditorStore, ["setEditorMessage"]),

        renderImage(message: IMessageItem) {
            if (message.autoMessageType === AutoGeneratedMessageEnum.MissedCall) {
                return require("../../../../../assets/images/chat/missed-audio-call-filled.svg");
            } else if (message.autoMessageType === AutoGeneratedMessageEnum.MissedVideoCall) {
                return require("../../../../../assets/images/chat/missed-video-call-filled.svg");
            } else if (
                [
                    AutoGeneratedMessageEnum.CallEnded,
                    AutoGeneratedMessageEnum.CallStarted,
                    AutoGeneratedMessageEnum.CallDeclined
                ].includes(message.autoMessageType)
            ) {
                return require("../../../../../assets/images/chat/audio-call-filled.svg");
            } else if (
                [AutoGeneratedMessageEnum.MeetingStarted, AutoGeneratedMessageEnum.MeetingEnded].includes(
                    message.autoMessageType
                )
            ) {
                return require("../../../../../assets/images/chat/video-call-filled.svg");
            } else {
                return "";
            }
        },
        handleEventPropagation(event: PointerEvent) {
            const target = event.target as HTMLElement;
            if (target.tagName === "A") {
                event.stopPropagation();
            }
        },
        updatePlaceholdersList() {
            if (this.message?.placeHoldersList?.length > 0) {
                this.message.placeHoldersList.forEach((item: IMessagePlaceholder) => {
                    if (item.type === PlaceholdersListEnum.TaggedUser) {
                        this.taggedUsersList.push(item);
                    } else if (
                        [PlaceholdersListEnum.ShareUser, PlaceholdersListEnum.SharePatient].includes(item.type)
                    ) {
                        this.sharedProfilesList.push(item);
                    }
                });
            }
        },
        async redirectToProfile(profile: any) {
            let url = "";
            const isClinician = this._permissionManager.checkPermission(
                ParentPermissionEnum.clinician,
                PermissionActionEnum.canView
            );
            if (profile.type === 2) {
                if (profile.applicationId === ApplicationEnum.AxxessHospiceFE) {
                    url = `${getHospiceRedirectionUrl(profile.agencyId)}/employees/employee-center/${profile.id}?accountId=${profile.agencyId}`;
                } else if (profile.applicationId === ApplicationEnum.AxxessPalliative) {
                    url = `${config.palliativeUrl}/employees/employee-center/${profile.id}?accountId=${profile.agencyId}`;
                } else {
                    try {
                        const redirectResponse = await this._navigationService.navigateToEmployee({
                            agencyId: profile.agencyId,
                            applicationId: profile.applicationId,
                            isClinician: isClinician,
                            employeeId: profile.id
                        });
                        url = redirectResponse.redirectUrl;
                    } catch (error: any) {
                        if (error?.status === 403) {
                            const model = {
                                title: `Access Denied`,
                                message:
                                    "You cannot access the Employee Profile as you don't have access to the agency.",
                                buttonText: "Close"
                            };
                            this.dialogRef = this.$dialog.open(Alert, {
                                props: {
                                    modal: true,
                                    showHeader: false,
                                    contentStyle: {
                                        width: "300px",
                                        overflowX: "hidden"
                                    }
                                },
                                data: { model }
                            });
                            return;
                        } else {
                            console.error("Unexpected error:", error);
                        }
                    }
                }
            } else {
                if (profile.applicationId === ApplicationEnum.AxxessHospiceFE) {
                    url = `${getHospiceRedirectionUrl(profile.agencyId)}/patients/patient-chart/${profile.id}?accountId=${profile.agencyId}`;
                } else {
                    try {
                        const redirectResponse = await this._navigationService.navigateToPatient({
                            agencyId: profile.agencyId,
                            applicationId: profile.applicationId,
                            isClinician: isClinician,
                            patientId: profile.id
                        });
                        url = redirectResponse.redirectUrl;
                    } catch (error: any) {
                        if (error?.status === 403) {
                            const model = {
                                title: `Access Denied`,
                                message:
                                    "You cannot access the Patient Profile as you don't have access to the agency.",
                                buttonText: "Close"
                            };
                            this.dialogRef = this.$dialog.open(Alert, {
                                props: {
                                    modal: true,
                                    showHeader: false,
                                    contentStyle: {
                                        width: "300px",
                                        overflowX: "hidden"
                                    }
                                },
                                data: { model }
                            });
                            return;
                        } else {
                            console.error("Unexpected error:", error);
                        }
                    }
                }
            }
            window.open(url, "_blank");
        },
        async renderAsset(conversationId: string, messageId: string, assetIds: string[]) {
            try {
                this.assetLoading = true;
                const assests = await this._messageService.getAssets(conversationId, messageId, assetIds);
                this.fetchedAssets = assests.map((asset: any) => ({ ...asset, showDownloadBtn: false }));
            } catch (error) {
                console.error("Error fetching assets:", error);
            } finally {
                this.assetLoading = false;
            }
        },
        async downloadAsset(asset: any, event: Event) {
            event.preventDefault();
            event.stopPropagation();
            asset.showDownloadBtn = false;
            try {
                const response = await fetch(asset.url);
                if (!response.ok) {
                    throw new Error(`Download failed: ${response.statusText}`);
                }
                const blob = await response.blob();
                const downloadUrl = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = downloadUrl;
                link.download = asset.fileName || "download";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(downloadUrl);
            } catch (error) {
                console.error("Error downloading file:", error);
                asset.showDownloadBtn = true;
            }
        },
        isImage(mimeType: string): boolean {
            return mimeType.startsWith("image/");
        },
        formatFileSize(size: number) {
            const units = ["B", "KB", "MB", "GB", "TB"];
            let unitIndex = 0;
            let formattedSize = size;
            while (formattedSize > 1024 && unitIndex < units.length - 1) {
                formattedSize /= 1024;
                unitIndex++;
            }
            return `${formattedSize.toFixed(2)} ${units[unitIndex]}`;
        },
        replyToMessage() {
            this.setEditorMessage({
                ...this.editorMessage,
                replyMessage: { ...this.message, text: this.formattedMessage }
            });
        },
        formatReplyToMsgText(message: IReplyToMessage) {
            const { text } = formatTaggedUserMessage(
                message.text,
                message.placeHoldersList,
                this.loggedInUserDetails.loginId
            );
            return text;
        },
        setRepliedMsgProfiles() {
            const { sharedProfiles } = sortPlaceholdersList(this.message.replyToMessage.placeHoldersList);
            this.replyMsgProfiles = sharedProfiles;
        },
        handleReplyMsgNavigation(event: Event) {
            event.stopPropagation();
            eventBus.emit("navigateToRepliedMsg", this.message.replyToMessage.messageId);
        },
        async handleEmoji(emoji: string) {
            if (!this.message) return;
            const hover = this.$refs.hoverActions as any;
            hover?.close();
            const msg = this.message;
            try {
                await this._messageService.sendReaction(msg.id, this.activeConversation.id, emoji);
            } catch (error) {
                console.error(error);
                this._toastrService.error({
                    title: `Error - Failed`,
                    message: `Failed to send reaction. Please try again.`
                });
            }
        },
        closeEmojiPicker() {
            const picker = this.$refs.emojiPicker as any;
            if (picker?.showPicker) {
                picker.showPicker = false;
            }
        }
    }
});
